import React from "react";
import {
  Close as CloseIcon,
  Person,
  RadioButtonChecked,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";
import patterbackground from "./pattern.jpeg";
import PhoneNumberField from "../../../../components/ui/PhoneNumberField";
import CustomTextField from "../../../../components/ui/Textfield";
import { toast } from "react-toastify";

export interface ClientInfo {
  fullName: string;
  whatsappNumber: string;
}

export interface PastorInfo {
  fullName: string;
  whatsappNumber: string;
}

interface ClientInfoModalProps {
  open: boolean;
  onClose: () => void;
  userType: "pastor" | "booking-for-pastor";
  onUserTypeChange: (type: "pastor" | "booking-for-pastor") => void;
  clientInfo: ClientInfo;
  onClientInfoChange: (info: ClientInfo) => void;
  pastorInfo: PastorInfo;
  onPastorInfoChange: (info: PastorInfo) => void;
  onSubmit: () => void;
}

export const ClientInfoModal: React.FC<ClientInfoModalProps> = ({
  open,
  onClose,
  userType,
  onUserTypeChange,
  clientInfo,
  onClientInfoChange,
  pastorInfo,
  onPastorInfoChange,
  onSubmit,
}) => {
    const theme = useTheme();
      const colors = tokens(theme.palette.mode);

  const handleSubmit = () => {
    
    if (!clientInfo.fullName || !clientInfo.whatsappNumber) {
      toast.error("Please fill in your information");
      return;
    }

    if (
      userType === "booking-for-pastor" &&
      (!pastorInfo.fullName || !pastorInfo.whatsappNumber)
    ) {
      toast.error("Please fill in pastor information")
      return;
    }

    onSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: "#614338",
          color: "white",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundRepeat: "repeat",
            backgroundImage: `url(${patterbackground})`,
            backgroundSize: "contain",
            opacity: 0.1,
            zIndex: 0,
          }}
        />
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            Apply for a Free Stream
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            mt: 1,
            color: colors.primary[500],
            position: "relative",
            zIndex: 1,
          }}
        >
          Who is booking this service?
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3, mt: 1 }}>
        {/* User Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
          <RadioGroup
            value={userType}
            onChange={(e) =>
              onUserTypeChange(
                e.target.value as "pastor" | "booking-for-pastor",
              )
            }
          >
            <FormControlLabel
              value="pastor"
              control={
                <Radio
                  checkedIcon={
                    <RadioButtonChecked sx={{ color: colors.primary[400] }} />
                  }
                  icon={<RadioButtonUnchecked />}
                />
              }
              label={
                <Typography
                  sx={{ fontWeight: userType === "pastor" ? 600 : 400 }}
                >
                  I am the pastor
                </Typography>
              }
              sx={{
                mb: 1,
                borderRadius: 2,
                borderColor:
                  userType === "pastor" ? colors.primary[400] : "divider",
                bgcolor:
                  userType === "pastor"
                    ? "rgba(97, 67, 56, 0.05)"
                    : "transparent",
                transition: "all 0.2s",
              }}
            />
            <FormControlLabel
              value="booking-for-pastor"
              control={
                <Radio
                  checkedIcon={
                    <RadioButtonChecked sx={{ color: colors.primary[400] }} />
                  }
                  icon={<RadioButtonUnchecked />}
                />
              }
              label={
                <Typography
                  sx={{
                    fontWeight: userType === "booking-for-pastor" ? 600 : 400,
                  }}
                >
                  {"I'm booking for a pastor"}
                </Typography>
              }
              sx={{
                borderRadius: 2,
                borderColor:
                  userType === "booking-for-pastor"
                    ? colors.primary[400]
                    : "divider",
                bgcolor:
                  userType === "booking-for-pastor"
                    ? "rgba(97, 67, 56, 0.05)"
                    : "transparent",
                transition: "all 0.2s",
              }}
            />
          </RadioGroup>
        </FormControl>

        {/* Client Info Section */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
        >
          {userType === "pastor"
            ? "Your Information"
            : "Your Contact Information"}
        </Typography>

        <CustomTextField
          fullWidth
          label="Enter Full Name*"
          placeholder="e.g. John Doe"
          value={clientInfo.fullName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onClientInfoChange({ ...clientInfo, fullName: e.target.value })
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <PhoneNumberField
          label="Phone Number*"
          placeholder="+263 77 123 4567"
          value={clientInfo.whatsappNumber}
          onChange={(value: string) =>
            onClientInfoChange({ ...clientInfo, whatsappNumber: value })
          }
        />

        {/* Pastor Info Section - Conditional */}
        {userType === "booking-for-pastor" && (
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
            >
              {"Pastor's Information"}
            </Typography>

            <CustomTextField
              fullWidth
              label="Pastor's Full Name*"
              placeholder="e.g. Rev. James Smith"
              value={pastorInfo.fullName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onPastorInfoChange({ ...pastorInfo, fullName: e.target.value })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <PhoneNumberField
              label="Pastor's Number*"
              placeholder="+263 77 123 4567"
              value={pastorInfo.whatsappNumber}
              onChange={(value: string) =>
                onPastorInfoChange({ ...pastorInfo, whatsappNumber: value })
              }
            />
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: "text.primary",
              borderColor: "divider",
              boxShadow: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: colors.primary[400],
              color: "white",
              "&:hover": {
                background: "#4d362d",
              },
            }}
          >
            Increase your chances
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
